import React, { useEffect, useRef, useState } from "react";
import {
  FaWhatsapp, FaChartLine, FaUsers,
  FaStar, FaUtensils, FaReceipt,
} from "react-icons/fa";

/* ─────────────────────────────────────────────
   FONT IMPORT ONLY — no layout in <style>
───────────────────────────────────────────── */
const FontLink = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,800;0,9..144,900;1,9..144,800;1,9..144,900&family=Plus+Jakarta+Sans:wght@300;400;500;600&display=swap');
    .wz-sparkline-path {
      stroke-dasharray: 320;
      stroke-dashoffset: 320;
      transition: stroke-dashoffset 1.4s cubic-bezier(.22,.68,0,1.2) .3s;
    }
    .wz-spark-visible .wz-sparkline-path { stroke-dashoffset: 0; }
    .wz-card-hover { transition: transform .22s, box-shadow .22s, border-color .22s; }
    .wz-card-hover:hover { transform: translateY(-3px); box-shadow: 0 16px 48px rgba(108,187,35,.11) !important; border-color: #6cbb23 !important; }
    .wz-icon-wrap { transition: background .2s, border-color .2s; }
    .wz-card-hover:hover .wz-icon-wrap { background: #6cbb23 !important; border-color: #6cbb23 !important; }
    .wz-card-hover:hover .wz-icon-svg { color: #fff !important; }
    .wz-pill-hover { transition: border-color .18s; }
    .wz-pill-hover:hover { border-color: #6cbb23 !important; }
    .wz-blink { animation: wz-blink 1.6s ease-in-out infinite; }
    @keyframes wz-blink { 0%,100%{opacity:1} 50%{opacity:.3} }
  `}</style>
);

/* ─────────────────────────────────────────────
   TOKENS
───────────────────────────────────────────── */
const G = "#6cbb23";
const T = { zh: { fontFamily:"'Fraunces',Georgia,serif" }, zb: { fontFamily:"'Plus Jakarta Sans',sans-serif" } };

/* ─────────────────────────────────────────────
   HOOKS
───────────────────────────────────────────── */
function useWindowWidth() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

function useReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setTimeout(() => setVis(true), delay); obs.disconnect(); } },
      { threshold: 0.08 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return { ref, vis };
}

/* ─────────────────────────────────────────────
   SHARED ATOMS
───────────────────────────────────────────── */
const cardBase = (extra: React.CSSProperties = {}): React.CSSProperties => ({
  background: "#fff",
  border: "1.5px solid #efefea",
  borderRadius: 20,
  padding: 28,
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 2px 12px rgba(0,0,0,.05)",
  ...extra,
});

const revealStyle = (vis: boolean, delay = 0): React.CSSProperties => ({
  opacity: vis ? 1 : 0,
  transform: vis ? "translateY(0)" : "translateY(22px)",
  transition: `opacity .58s ${delay}ms cubic-bezier(.22,.68,0,1.2), transform .58s ${delay}ms cubic-bezier(.22,.68,0,1.2)`,
});

const IconWrap = ({ icon: Icon, dark = false }: { icon: React.ElementType; dark?: boolean }) => (
  <div className="wz-icon-wrap" style={{
    width: 42, height: 42, borderRadius: 12, flexShrink: 0,
    background: dark ? "rgba(108,187,35,.15)" : "#f0f9e6",
    border: `1.5px solid ${dark ? "rgba(108,187,35,.3)" : "#d4efb5"}`,
    display: "flex", alignItems: "center", justifyContent: "center",
  }}>
    <Icon className="wz-icon-svg" style={{ color: G, fontSize: 16 }} />
  </div>
);

const CardTag = ({ label }: { label: string }) => (
  <span style={{ ...T.zb, fontSize: 10, fontWeight: 700, color: "#bbb", letterSpacing: ".1em", textTransform: "uppercase" as const }}>
    {label}
  </span>
);

const CardTitle = ({ children }: { children: React.ReactNode }) => (
  <h3 style={{ ...T.zh, fontSize: 20, fontWeight: 900, fontStyle: "italic", color: "#111", lineHeight: 1.1, marginTop: 3, marginBottom: 0 }}>
    {children}
  </h3>
);

const CardBody = ({ children }: { children: React.ReactNode }) => (
  <p style={{ ...T.zb, fontSize: 13, color: "#888", lineHeight: 1.75, fontWeight: 300, marginTop: 10 }}>
    {children}
  </p>
);

const GhostText = ({ children, size = 140, bottom = -20, right = 10 }: any) => (
  <div style={{
    position: "absolute", bottom, right,
    ...T.zh, fontSize: size, fontWeight: 900, fontStyle: "italic",
    color: G, opacity: .035, lineHeight: 1,
    userSelect: "none", pointerEvents: "none",
  }}>{children}</div>
);

/* ─────────────────────────────────────────────
   CARD VISUALS
───────────────────────────────────────────── */

/** WhatsApp live chat preview */
const ChatPreview = () => (
  <div style={{ background: "#0d1a0d", borderRadius: 14, padding: "12px 14px" }}>
    {/* Header */}
    <div style={{ display: "flex", alignItems: "center", gap: 8, paddingBottom: 10, borderBottom: "1px solid rgba(255,255,255,.07)", marginBottom: 10 }}>
      <div style={{ width: 26, height: 26, borderRadius: "50%", background: G, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <FaWhatsapp style={{ color: "#fff", fontSize: 12 }} />
      </div>
      <div>
        <div style={{ ...T.zb, fontSize: 11, fontWeight: 600, color: "#fff" }}>Zafflo Orders</div>
        <div style={{ ...T.zb, fontSize: 9, color: G, display: "flex", alignItems: "center", gap: 4 }}>
          <span className="wz-blink" style={{ width: 5, height: 5, borderRadius: "50%", background: G, display: "inline-block" }} />
          online
        </div>
      </div>
    </div>
    {/* Bubbles */}
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ background: "#1e2f1e", borderRadius: "12px 12px 12px 2px", padding: "7px 10px", maxWidth: "82%", alignSelf: "flex-start" }}>
        <span style={{ ...T.zb, fontSize: 11, color: "rgba(255,255,255,.8)", lineHeight: 1.45 }}>Table 4 — 2× Paneer Tikka + 1× Naan 🙌</span>
      </div>
      <div style={{ background: `linear-gradient(135deg,#4d8f10,${G})`, borderRadius: "12px 12px 2px 12px", padding: "7px 10px", maxWidth: "80%", alignSelf: "flex-end" }}>
        <span style={{ ...T.zb, fontSize: 11, color: "#fff", lineHeight: 1.45 }}>
          Order confirmed ✓<br />
          <span style={{ opacity: .75 }}>₹520 · Est. 12 min</span>
        </span>
      </div>
    </div>
    <div style={{ textAlign: "center", marginTop: 8 }}>
      <span style={{ ...T.zb, fontSize: 9, color: "rgba(255,255,255,.22)" }}>Arrives instantly · No app needed</span>
    </div>
  </div>
);

/** Sparkline + stat chips */
const SparkCard = ({ visible }: { visible: boolean }) => (
  <div>
    <svg width="100%" height="60" viewBox="0 0 220 60" fill="none" preserveAspectRatio="none" className={visible ? "wz-spark-visible" : ""}>
      <defs>
        <linearGradient id="wz-sg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={G} stopOpacity=".16" />
          <stop offset="100%" stopColor={G} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d="M0 50 C20 46,36 56,56 40 S92 18,116 28 S156 42,180 16 S200 6,220 10" fill="url(#wz-sg)" />
      <path className="wz-sparkline-path" d="M0 50 C20 46,36 56,56 40 S92 18,116 28 S156 42,180 16 S200 6,220 10"
        stroke={G} strokeWidth="2.5" fill="none" strokeLinecap="round" />
      {([[56,40],[116,28],[180,16],[220,10]] as [number,number][]).map(([cx,cy],i) => (
        <circle key={i} cx={cx} cy={cy} r="3.5" fill={G} stroke="#fff" strokeWidth="1.5" />
      ))}
    </svg>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 8 }}>
      {[{ v:"₹48K", l:"This week" },{ v:"+18%", l:"vs last week" },{ v:"142", l:"Orders" }].map(({ v, l }) => (
        <div key={l} style={{ background: "#f8f8f5", borderRadius: 10, padding: "8px 10px" }}>
          <div style={{ ...T.zh, fontSize: 16, fontWeight: 900, color: "#111", lineHeight: 1 }}>{v}</div>
          <div style={{ ...T.zb, fontSize: 10, color: "#bbb", marginTop: 3 }}>{l}</div>
        </div>
      ))}
    </div>
  </div>
);

/** Avatar stack */
const AvatarStack = () => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14 }}>
    <div style={{ display: "flex", alignItems: "center" }}>
      {[
        { bg:"#fde68a", color:"#92400e", l:"R" },
        { bg:"#bfdbfe", color:"#1e40af", l:"S" },
        { bg:"#d9f99d", color:"#3b6d11", l:"P" },
        { bg:"#fecdd3", color:"#9f1239", l:"A" },
        { bg:"#f0f0ec", color:"#888",    l:"+99" },
      ].map(({ bg, color, l }, i) => (
        <div key={i} style={{
          width: 30, height: 30, borderRadius: "50%", border: "2px solid #fff",
          marginLeft: i === 0 ? 0 : -9, zIndex: 5 - i, background: bg, color,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: i === 4 ? 9 : 11, fontWeight: 700, flexShrink: 0, ...T.zb,
        }}>{l}</div>
      ))}
    </div>
    <div>
      <div style={{ ...T.zb, fontSize: 12, fontWeight: 600, color: "#111" }}>Growing every day</div>
      <div style={{ ...T.zb, fontSize: 11, color: "#aaa" }}>Auto-captured from orders</div>
    </div>
  </div>
);

/** Star rating */
const StarRating = () => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14 }}>
    <div style={{ display: "flex", gap: 2 }}>
      {[1,2,3,4,5].map(i => (
        <svg key={i} width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M7 1.5L8.5 4.8l3.5.5-2.5 2.4.6 3.4L7 9.4l-3.1 1.7.6-3.4L2 5.3l3.5-.5L7 1.5z"
            fill={i<=4?G:"#f0f0ec"} stroke={i<=4?G:"#ddd"} strokeWidth=".5"/>
        </svg>
      ))}
    </div>
    <span style={{ ...T.zb, fontSize: 13, fontWeight: 700, color: "#111" }}>4.8 / 5</span>
    <span style={{ ...T.zb, fontSize: 11, color: "#bbb" }}>avg guest rating</span>
  </div>
);

/** GST invoice rows */
const InvoicePreview = () => (
  <div style={{ background: "#fafaf8", border: "1px solid #f0f0ec", borderRadius: 14, padding: "12px 16px" }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
      <span style={{ ...T.zb, fontSize: 10, fontWeight: 700, color: G, letterSpacing: ".06em", textTransform: "uppercase" as const }}>
        Invoice #INV-1482
      </span>
      <span style={{ ...T.zb, fontSize: 10, background: "#fef9c3", color: "#854d0e", borderRadius: 20, padding: "2px 8px", fontWeight: 600 }}>
        Filed ✓
      </span>
    </div>
    {[
      { l:"Subtotal",  v:"₹4,082", bold:false },
      { l:"CGST 9%",  v:"₹389",   bold:false },
      { l:"SGST 9%",  v:"₹389",   bold:false },
      { l:"Total",    v:"₹4,860", bold:true  },
    ].map(({ l, v, bold }) => (
      <div key={l} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"7px 0", borderBottom:"1px solid #f0f0ec" }}>
        <span style={{ ...T.zb, fontSize: 12, color: bold ? "#333" : "#aaa", fontWeight: bold ? 600 : 400 }}>{l}</span>
        <span style={{ ...T.zh, fontSize: bold ? 18 : 13, fontWeight: 900, color: bold ? "#111" : "#555" }}>{v}</span>
      </div>
    ))}
  </div>
);

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
const WhyZafflo = () => {
  const width = useWindowWidth();
  const isMobile  = width < 640;
  const isTablet  = width >= 640 && width < 960;
  const isDesktop = width >= 960;

  // Reveals
  const hdr  = useReveal(0);
  const c1   = useReveal(50);
  const c2   = useReveal(120);
  const c3   = useReveal(80);
  const c4   = useReveal(150);
  const c5   = useReveal(220);
  const c6   = useReveal(100);
  const foot = useReveal(0);

  // Layout helpers
  const gap = isMobile ? 10 : 14;
  const col = (spans: number) => {
    if (isMobile) return "100%";
    if (isTablet) return spans >= 7 ? "100%" : "calc(50% - 7px)";
    // Desktop: 12-col grid approximation
    return `calc(${(spans/12)*100}% - ${gap * (1 - spans/12)}px)`;
  };

  return (
    <section style={{ background:"#fafaf8", position:"relative", overflow:"hidden", padding:"60px 0 42px", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <FontLink />

      {/* Grid texture */}
      <div style={{
        position:"absolute", inset:0, pointerEvents:"none",
        backgroundImage:"linear-gradient(#efefea 1px,transparent 1px),linear-gradient(90deg,#efefea 1px,transparent 1px)",
        backgroundSize:"48px 48px", opacity:.5,
      }}/>

      {/* Glow */}
      <div style={{ position:"absolute", bottom:-80, right:-80, width:500, height:500, borderRadius:"50%", background:"radial-gradient(ellipse,rgba(108,187,35,.07) 0%,transparent 70%)", pointerEvents:"none" }}/>

      <div style={{ maxWidth:1100, margin:"0 auto", padding:"0 24px", position:"relative", zIndex:1 }}>

        {/* ── HEADER ── */}
        <div ref={hdr.ref} style={{ ...revealStyle(hdr.vis), textAlign:"center", marginBottom:52 }}>
         
          <h2 style={{ ...T.zh, fontSize:"clamp(30px,3.8vw,50px)", fontWeight:900, fontStyle:"italic", color:"#111", letterSpacing:"-0.03em", lineHeight:1.27, marginBottom:16 }}>
            Everything your business needs.{" "}
            <mark style={{ background:G, color:"#fff", padding:"1px 12px 5px", borderRadius:6, fontStyle:"italic", display:"inline-block" }}>
              Nothing it doesn't.
            </mark>
          </h2>
          <p style={{ ...T.zb, fontSize:15, color:"#888", fontWeight:300, maxWidth:440, margin:"0 auto", lineHeight:1.75 }}>
            Zafflo brings orders, analytics, billing, appointments, and customer marketing into one WhatsApp-powered system.
          </p>
        </div>

        {/* ── BENTO ROW 1 ── */}
        <div style={{ display:"flex", gap, marginBottom:gap, flexWrap: isDesktop ? "nowrap" as const : "wrap" as const }}>

          {/* CARD 1 — WhatsApp Ordering (wide, dark) */}
          <div ref={c1.ref} className="wz-card-hover" style={{
            ...revealStyle(c1.vis, 50),
            ...cardBase({ background:"#0c1a0c", border:"1.5px solid #1a2e1a" }),
            flex: isDesktop ? "0 0 calc(58% - 7px)" : "1 1 100%",
            minWidth: 0,
          }}>
            <GhostText size={160} bottom={-24} right={-8}>W</GhostText>
            <div style={{ display:"flex", flexDirection: isMobile ? "column" : "row", gap:24, height:"100%" }}>
              {/* Left text */}
              <div style={{ flex:1, minWidth:0, display:"flex", flexDirection:"column", justifyContent:"space-between", gap:16 }}>
                <div>
                  <IconWrap icon={FaWhatsapp} dark />
                  <h3 style={{ ...T.zh, fontSize:22, fontWeight:900, fontStyle:"italic", color:"#fff", lineHeight:1.1, margin:"16px 0 10px" }}>
                    Orders Land on WhatsApp Instantly
                  </h3>
                  <p style={{ ...T.zb, fontSize:13, color:"rgba(255,255,255,.5)", lineHeight:1.75, fontWeight:300 }}>
                    No app downloads. No training. Customers scan the QR, pick their order, and it arrives directly in your WhatsApp with full details.
                  </p>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                  <span style={{ width:6, height:6, borderRadius:"50%", background:G, display:"inline-block", flexShrink:0 }}/>
                  <span style={{ ...T.zb, fontSize:11, color:"rgba(255,255,255,.35)" }}>Works on any smartphone · Zero friction for customers</span>
                </div>
              </div>
              {/* Right chat */}
              <div style={{ flex:"0 0 auto", width: isMobile ? "100%" : 220 }}>
                <ChatPreview />
              </div>
            </div>
          </div>

          {/* CARD 2 — Analytics */}
          <div ref={c2.ref} className="wz-card-hover" style={{
            ...revealStyle(c2.vis, 120),
            ...cardBase(),
            flex: isDesktop ? "1 1 0" : "1 1 100%",
            minWidth: 0,
          }}>
            <GhostText size={90} bottom={-10} right={4}>↑</GhostText>
            <div style={{ display:"flex", alignItems:"flex-start", gap:12, marginBottom:12 }}>
              <IconWrap icon={FaChartLine} />
              <div>
                <CardTag label="Analytics" />
                <CardTitle>Sales Insights at a Glance</CardTitle>
              </div>
            </div>
            <CardBody>Track revenue, hot-selling items, and peak hours — stock smarter and market better.</CardBody>
            <SparkCard visible={c2.vis} />
          </div>
        </div>

        {/* ── BENTO ROW 2 ── */}
        <div style={{ display:"flex", gap, marginBottom:gap, flexWrap:"wrap" as const }}>

          {/* CARD 3 — Upsell */}
          <div ref={c3.ref} className="wz-card-hover" style={{
            ...revealStyle(c3.vis, 80),
            ...cardBase(),
            flex: isDesktop ? "1 1 0" : isTablet ? "1 1 calc(50% - 7px)" : "1 1 100%",
            minWidth: 0,
          }}>
            <div style={{ display:"flex", alignItems:"flex-start", gap:12, marginBottom:12 }}>
              <IconWrap icon={FaUtensils} />
              <div>
                <CardTag label="Upsell" />
                <CardTitle>Bigger Orders, Naturally</CardTitle>
              </div>
            </div>
            <CardBody>Customers explore photos, descriptions, and combos — and naturally order more than from a paper menu.</CardBody>
            <div style={{ marginTop:16, display:"flex", alignItems:"baseline", gap:8 }}>
              <span style={{ ...T.zh, fontSize:36, fontWeight:900, color:G, lineHeight:1 }}>+23%</span>
              <span style={{ ...T.zb, fontSize:12, color:"#aaa" }}>avg order value increase</span>
            </div>
          </div>

          {/* CARD 4 — Customer DB */}
          <div ref={c4.ref} className="wz-card-hover" style={{
            ...revealStyle(c4.vis, 150),
            ...cardBase(),
            flex: isDesktop ? "1 1 0" : isTablet ? "1 1 calc(50% - 7px)" : "1 1 100%",
            minWidth: 0,
          }}>
            <div style={{ display:"flex", alignItems:"flex-start", gap:12, marginBottom:12 }}>
              <IconWrap icon={FaUsers} />
              <div>
                <CardTag label="CRM" />
                <CardTitle>Auto-Built Customer Database</CardTitle>
              </div>
            </div>
            <CardBody>Every WhatsApp order captures the customer's number. Build a contact list and send targeted offers for repeat business.</CardBody>
            <AvatarStack />
          </div>

          {/* CARD 5 — Experience */}
          <div ref={c5.ref} className="wz-card-hover" style={{
            ...revealStyle(c5.vis, 220),
            ...cardBase(),
            flex: isDesktop ? "1 1 0" : "1 1 100%",
            minWidth: 0,
          }}>
            <div style={{ display:"flex", alignItems:"flex-start", gap:12, marginBottom:12 }}>
              <IconWrap icon={FaStar} />
              <div>
                <CardTag label="Experience" />
                <CardTitle>Happier Guests, Every Visit</CardTitle>
              </div>
            </div>
            <CardBody>Faster service, zero order errors, and a seamless experience that keeps customers coming back again and again.</CardBody>
            <StarRating />
          </div>
        </div>

        {/* ── BENTO ROW 3 — GST full-width ── */}
        <div ref={c6.ref} className="wz-card-hover" style={{
          ...revealStyle(c6.vis, 100),
          ...cardBase(),
        }}>
          <GhostText size={120} bottom={-16} right={16}>GST</GhostText>
          <div style={{ display:"flex", flexDirection: isMobile ? "column" : "row", gap:32, alignItems:"flex-start" }}>

            {/* Left — copy */}
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:"flex", alignItems:"flex-start", gap:12, marginBottom:16 }}>
                <IconWrap icon={FaReceipt} />
                <div>
                  <CardTag label="Billing" />
                  <CardTitle>GST-Ready Invoices & Reports</CardTitle>
                </div>
              </div>
              <p style={{ ...T.zb, fontSize:14, color:"#777", lineHeight:1.8, fontWeight:300, marginBottom:18 }}>
                Generate professional, GST-compliant invoices automatically from every order.
                Export monthly reports for your accountant in one click — no manual entry, ever.
              </p>
              <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                {["Auto CGST / SGST","One-click export","PDF & Excel","Audit-ready"].map(tag => (
                  <span key={tag} style={{
                    ...T.zb, display:"inline-flex", alignItems:"center", gap:5,
                    background:"#f0f9e6", border:"1.5px solid #c8e89c", borderRadius:999,
                    padding:"4px 11px", fontSize:11, fontWeight:600, color:"#3b6d11",
                  }}>
                    <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                      <path d="M1.5 4.5l2 2L7.5 2" stroke="#3b6d11" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Right — invoice preview */}
            <div style={{ flex: isDesktop ? "0 0 320px" : "1 1 100%", minWidth:0 }}>
              <InvoicePreview />
            </div>
          </div>
        </div>

        {/* ── TRUST STRIP ── */}
        <div ref={foot.ref} style={{ ...revealStyle(foot.vis), marginTop:44, display:"flex", justifyContent:"center", gap:10, flexWrap:"wrap" as const }}>
          {[
            { e:"🇮🇳", t:"Built for Indian businesses"   },
            { e:"🍽️", t:"Restaurants · Cafés · Hotels"   },
            { e:"💇",  t:"Salons · Spas · Any business"  },
          ].map(({ e, t }) => (
            <span key={t} className="wz-pill-hover" style={{
              ...T.zb, display:"inline-flex", alignItems:"center", gap:8,
              background:"#fff", border:"1.5px solid #efefea", borderRadius:999,
              padding:"9px 20px", fontSize:12, fontWeight:500, color:"#666",
            }}>
              <span style={{ fontSize:14 }}>{e}</span>{t}
            </span>
          ))}
        </div>

      </div>
    </section>
  );
};

export default WhyZafflo;